import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Wallet, Coins, Users, User, ArrowDown, ChevronDown, Lock, Award, Check } from "lucide-react";
import { fmtCents } from "./util";

const WALLET_COLORS = { main: "#2DD4BF", staking: "#0E9E90", referral: "#00E676" };

/* Donut showing distribution across the three wallets */
export function WalletAllocationDonut({ wallets }) {
  const data = [
    { key: "main", name: "Main", value: Math.max(0, wallets?.main?.available_cents || 0) },
    { key: "staking", name: "Staking", value: Math.max(0, wallets?.staking?.available_cents || 0) },
    { key: "referral", name: "Referral", value: Math.max(0, wallets?.referral?.available_cents || 0) },
  ];
  const total = data.reduce((a, b) => a + b.value, 0);
  return (
    <div className="rounded-xl border border-white/5 bg-[#0A0B10] p-5" data-testid="wallet-allocation">
      <h3 className="font-semibold text-white mb-1">Wallet Allocation</h3>
      <p className="text-xs text-slate-500 mb-4">Balance distribution across wallets</p>
      <div className="relative h-44">
        {total > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={54} outerRadius={78} paddingAngle={3} stroke="none">
                {data.map((d) => <Cell key={d.key} fill={WALLET_COLORS[d.key]} />)}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full grid place-items-center text-xs text-slate-600 font-mono">No balances yet</div>
        )}
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Total</p>
            <p className="font-mono text-lg text-white">{fmtCents(total)}</p>
          </div>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {data.map((d) => (
          <div key={d.key} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-2 text-slate-300">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: WALLET_COLORS[d.key] }} /> {d.name}
            </span>
            <span className="font-mono text-white">{fmtCents(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Interactive wallet architecture diagram */
const ARCH = [
  { key: "main", label: "Main Wallet", icon: Wallet, source: "Daily Payouts", color: "#2DD4BF" },
  { key: "staking", label: "Staking Wallet", icon: Coins, source: "Staking Portfolios", color: "#0E9E90" },
  { key: "referral", label: "Referral Wallet", icon: Users, source: "Referrals & Bonuses", color: "#00E676" },
];
export function WalletArchitecture() {
  return (
    <div className="rounded-xl border border-white/5 bg-[#0A0B10] p-5" data-testid="wallet-architecture">
      <h3 className="font-semibold text-white mb-1">Wallet Architecture</h3>
      <p className="text-xs text-slate-500 mb-5">Where your earnings are routed</p>
      <div className="flex flex-col items-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-white">
          <User size={14} strokeWidth={1.5} className="text-[#2DD4BF]" /> You
        </span>
        <span className="h-6 w-px bg-white/10" />
        <div className="grid grid-cols-3 gap-3 w-full">
          {ARCH.map((a, i) => (
            <motion.div
              key={a.key}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-lg border border-white/10 bg-black/30 p-3 text-center"
            >
              <span className="grid place-items-center h-9 w-9 mx-auto rounded-lg mb-2" style={{ background: `${a.color}1a`, color: a.color }}>
                <a.icon size={16} strokeWidth={1.5} />
              </span>
              <p className="text-[11px] font-semibold text-white leading-tight">{a.label}</p>
              <span className="my-1.5 grid place-items-center text-slate-600"><ArrowDown size={12} /></span>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-500 leading-tight">{a.source}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Animated payout allocation visualization for Quantum Verse */
const PAYOUTS = {
  daily: [{ label: "Main Wallet", pct: 100, color: "#2DD4BF" }],
  staking: [{ label: "Staking Wallet", pct: 100, color: "#0E9E90" }],
  hybrid: [
    { label: "Main Wallet", pct: 25, color: "#2DD4BF" },
    { label: "Staking Wallet", pct: 75, color: "#0E9E90" },
  ],
};
export function PayoutAllocation({ type = "daily" }) {
  const alloc = PAYOUTS[type] || PAYOUTS.daily;
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-4" data-testid={`payout-alloc-${type}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-3">Payout Routing</p>
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-white/5">
        {alloc.map((a) => (
          <motion.div
            key={a.label}
            initial={{ width: 0 }} animate={{ width: `${a.pct}%` }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ background: a.color }}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
        {alloc.map((a) => (
          <span key={a.label} className="flex items-center gap-2 text-xs text-slate-300">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: a.color }} />
            {a.pct}% → {a.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* Portfolio progress bar */
export function PortfolioProgress({ pct = 0, label }) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div data-testid="portfolio-progress">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label || "Term Progress"}</span>
        <span className="font-mono text-xs text-[#2DD4BF]">{clamped.toFixed(0)}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }} whileInView={{ width: `${clamped}%` }} viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="h-full rounded-full bg-gradient-to-r from-[#0E9E90] to-[#2DD4BF]"
        />
      </div>
    </div>
  );
}

/* Interactive 3D virtual card */
export function VirtualCard3D({ holder = "MEMBER" }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    setTilt({ rx: -(py - 0.5) * 14, ry: (px - 0.5) * 18, mx: px * 100, my: py * 100 });
  };
  const reset = () => setTilt({ rx: 0, ry: 0, mx: 50, my: 50 });
  return (
    <div className="[perspective:1200px] w-full max-w-sm" data-testid="virtual-card-3d">
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        className="qs-card3d relative aspect-[1.586/1] w-full rounded-2xl p-6 overflow-hidden border border-white/10 shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
        style={{
          transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
          background: "linear-gradient(135deg, #14224F 0%, #0A0B10 55%, #08090F 100%)",
        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(320px circle at ${tilt.mx}% ${tilt.my}%, rgba(45,212,191,0.28), transparent 60%)` }}
        />
        <div className="absolute inset-0 qs-grid opacity-[0.12]" />
        <div className="relative flex h-full flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <span className="grid place-items-center h-7 w-7 rounded-md border border-[#2DD4BF]/40 bg-[#2DD4BF]/10">
                <span className="h-3 w-3 rounded-sm bg-[#2DD4BF] rotate-45" />
              </span>
              <span className="font-black text-sm text-white tracking-tight">Quantum<span className="text-[#2DD4BF]">Scalp</span></span>
            </div>
            <span className="font-mono text-[9px] uppercase tracking-widest rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-amber-300">Coming Soon</span>
          </div>
          <div className="h-8 w-11 rounded-md bg-gradient-to-br from-amber-200/80 to-amber-400/60" />
          <div>
            <p className="font-mono text-lg tracking-[0.2em] text-white/90">•••• •••• •••• 4C8D</p>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <p className="font-mono text-[8px] uppercase tracking-widest text-slate-400">Card Holder</p>
                <p className="font-mono text-xs text-white uppercase">{holder}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-[8px] uppercase tracking-widest text-slate-400">Virtual</p>
                <p className="font-mono text-xs text-[#2DD4BF]">USD</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Quantum Flex bonus cards */
const BONUSES = [
  { key: "fast-start", name: "Fast Start Bonus", desc: "Rewarded on early qualifying activity within your first cycle." },
  { key: "team-volume", name: "Team Volume Bonus", desc: "Scales with total verifiable volume across your organization." },
  { key: "direct-growth", name: "Direct Growth Bonus", desc: "Recognizes first-level expansion and active direct referrals." },
  { key: "quantum-points", name: "Global Quantum Points", desc: "Points accrue from team production and redeem for rewards." },
];
export function BonusCards() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="flex-bonus-cards">
      {BONUSES.map((b, i) => (
        <motion.div
          key={b.key}
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: i * 0.06 }}
          className="qs-beam rounded-xl border border-white/5 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-4"
        >
          <p className="text-sm font-semibold text-white">{b.name}</p>
          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{b.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}


/* Expandable rank panel (all 12 ranks) */
const RANK_BENEFITS = (level) => {
  const b = ["Access to 7-level referral commissions", "Referral wallet payouts"];
  if (level >= 3) b.push("Fast Start Bonus eligibility");
  if (level >= 5) b.push("Team Volume Bonus tier");
  if (level >= 7) b.push("Direct Growth Bonus", "Elevated Quantum Points multiplier");
  if (level >= 10) b.push("Leadership pool participation", "Priority support & recognition");
  return b;
};
export function RankPanel({ rk, state, progress = 0, open, onToggle }) {
  const stateStyles = {
    achieved: "border-[#00E676]/30 bg-[#00E676]/[0.04]",
    current: "border-[#0E9E90]/50 bg-[#0E9E90]/[0.06] qs-glow-blue",
    locked: "border-white/5 bg-[#0A0B10]",
  }[state];
  const StateIcon = state === "achieved" ? Check : state === "current" ? Award : Lock;
  const iconColor = state === "achieved" ? "text-[#00E676]" : state === "current" ? "text-[#2DD4BF]" : "text-slate-500";
  return (
    <div className={`rounded-xl border transition-colors ${stateStyles}`} data-testid={`rank-panel-${rk.level}`}>
      <button onClick={onToggle} data-testid={`rank-toggle-${rk.level}`} className="w-full flex items-center gap-3 p-4 text-left">
        <span className={`grid place-items-center h-10 w-10 rounded-lg border shrink-0 border-white/10 bg-black/30 ${iconColor}`}><StateIcon size={18} strokeWidth={1.6} /></span>
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#0E9E90]">Rank {rk.level}</p>
          <p className="font-semibold text-white truncate">{rk.name}</p>
        </div>
        {state === "current" && <span className="font-mono text-[9px] uppercase tracking-widest rounded-full border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 px-2 py-0.5 text-[#2DD4BF]">Current</span>}
        <ChevronDown size={18} className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-white/5 bg-black/30 p-3"><p className="text-[10px] uppercase font-mono text-slate-500">Team Sales Req.</p><p className="font-mono text-sm text-white mt-0.5">{fmtCents(rk.team_req)}</p></div>
                <div className="rounded-lg border border-white/5 bg-black/30 p-3"><p className="text-[10px] uppercase font-mono text-slate-500">Personal Sales Req.</p><p className="font-mono text-sm text-white mt-0.5">{fmtCents(rk.personal_req ?? rk.first_req)}</p></div>
              </div>
              <div className="rounded-lg border border-[#0E9E90]/20 bg-[#0E9E90]/[0.05] p-3">
                <p className="text-[10px] uppercase font-mono text-[#2DD4BF]">Rewards</p>
                <p className="text-sm text-white mt-1">{rk.reward}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-mono text-slate-500 mb-1.5">Benefits</p>
                <ul className="space-y-1">
                  {RANK_BENEFITS(rk.level).map((x) => (
                    <li key={x} className="flex items-center gap-2 text-xs text-slate-300"><Check size={13} className="text-[#00E676] shrink-0" /> {x}</li>
                  ))}
                </ul>
              </div>
              {state === "current" && (
                <div>
                  <div className="flex justify-between text-[10px] font-mono text-slate-500 mb-1"><span>Progress to next rank</span><span>{progress}%</span></div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#0E9E90] to-[#2DD4BF]" style={{ width: `${progress}%` }} /></div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* Animated vertical rank ladder — live position + exact sales left to level up */
export function RankLadder({ ranks = [], current, next, teamVol = 0, personalVol = 0, progress = 0 }) {
  const curLevel = current?.level || 1;
  const teamLeft = next ? Math.max(0, next.team_req - teamVol) : 0;
  const personalLeft = next ? Math.max(0, (next.personal_req ?? next.first_req ?? 0) - personalVol) : 0;
  const nextBonus = next?.reward_bonus_cents || 0;
  const ordered = [...ranks].sort((a, b) => b.level - a.level); // top rank at top
  return (
    <div data-testid="rank-ladder">
      {next && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="mb-5 rounded-xl border border-[#2DD4BF]/25 bg-gradient-to-br from-[#2DD4BF]/[0.07] to-transparent p-4" data-testid="rank-next-callout">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="text-[10px] uppercase font-mono tracking-widest text-[#2DD4BF]">Next Rank</p>
              <p className="font-bold text-white text-lg">{next.name}{nextBonus > 0 && <span className="ml-2 font-mono text-sm text-[#00E676]">+{fmtCents(nextBonus)} bonus</span>}</p>
            </div>
            <div className="text-right">
              <p className="font-mono text-2xl font-bold text-[#2DD4BF]">{progress}%</p>
              <p className="text-[10px] font-mono text-[#64748B]">qualified</p>
            </div>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden mb-3">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.9, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-[#0E9E90] to-[#2DD4BF]" />
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
              <p className="text-[10px] uppercase font-mono text-[#64748B]">Team sales to go</p>
              <p className={`font-mono text-sm mt-0.5 ${teamLeft === 0 ? "text-[#00E676]" : "text-white"}`}>{teamLeft === 0 ? "✓ Met" : fmtCents(teamLeft)}</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/30 p-2.5">
              <p className="text-[10px] uppercase font-mono text-[#64748B]">Personal sales to go</p>
              <p className={`font-mono text-sm mt-0.5 ${personalLeft === 0 ? "text-[#00E676]" : "text-white"}`}>{personalLeft === 0 ? "✓ Met" : fmtCents(personalLeft)}</p>
            </div>
          </div>
        </motion.div>
      )}
      <div className="relative pl-1">
        <div className="absolute left-[22px] top-3 bottom-3 w-px bg-white/8" />
        <div className="space-y-1.5">
          {ordered.map((rk, i) => {
            const state = rk.level < curLevel ? "achieved" : rk.level === curLevel ? "current" : "locked";
            const dot = state === "achieved" ? "bg-[#00E676] border-[#00E676]" : state === "current" ? "bg-[#2DD4BF] border-[#2DD4BF]" : "bg-[#0A0B10] border-white/15";
            const DotIcon = state === "achieved" ? Check : state === "current" ? Award : Lock;
            const bonus = rk.reward_bonus_cents || 0;
            return (
              <motion.div
                key={rk.level}
                initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: i * 0.03 }}
                data-testid={`ladder-rung-${rk.level}`}
                className={`relative flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                  state === "current" ? "border-[#2DD4BF]/50 bg-[#2DD4BF]/[0.06] qs-glow-blue"
                  : state === "achieved" ? "border-[#00E676]/20 bg-[#00E676]/[0.03]" : "border-white/5 bg-transparent"}`}>
                <span className={`relative z-10 grid place-items-center h-[26px] w-[26px] rounded-full border-2 shrink-0 ${dot}`}>
                  <DotIcon size={13} className={state === "locked" ? "text-slate-500" : "text-[#030305]"} strokeWidth={2.4} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`font-semibold truncate ${state === "locked" ? "text-slate-400" : "text-white"}`}>{rk.name}</p>
                    {state === "current" && <span className="font-mono text-[8px] uppercase tracking-widest rounded-full border border-[#2DD4BF]/40 bg-[#2DD4BF]/10 px-1.5 py-0.5 text-[#2DD4BF]">You are here</span>}
                    {bonus > 0 && <span className={`font-mono text-[9px] ${state === "locked" ? "text-slate-500" : "text-[#00E676]"}`}>+{fmtCents(bonus)}</span>}
                  </div>
                  <p className="font-mono text-[10px] text-[#64748B]">Rank {rk.level} · team {fmtCents(rk.team_req)} · personal {fmtCents(rk.personal_req ?? rk.first_req)}</p>
                </div>
                {state === "achieved" && <span className="font-mono text-[9px] uppercase text-[#00E676] shrink-0" data-testid={`ladder-claimed-${rk.level}`}>{bonus > 0 ? "Bonus claimed" : "Achieved"}</span>}
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/* Interactive 7-level organization tree */
export function OrgTree({ levels = [] }) {
  const [openLv, setOpenLv] = useState(1);
  const maxVol = Math.max(1, ...levels.map((l) => l.volume_cents || 0));
  return (
    <div className="space-y-2" data-testid="org-tree">
      {levels.map((lv) => {
        const on = openLv === lv.level;
        const dots = Math.min(24, lv.members || 0);
        const activeDots = Math.min(dots, lv.active || 0);
        return (
          <div key={lv.level} className={`rounded-xl border transition-colors ${on ? "border-[#0E9E90]/40 bg-[#0E9E90]/[0.05]" : "border-white/5 bg-black/20 hover:border-white/10"}`} style={{ marginLeft: `${(lv.level - 1) * 10}px` }}>
            <button onClick={() => setOpenLv(on ? null : lv.level)} data-testid={`org-level-${lv.level}`} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
              <span className="flex items-center gap-2.5 text-sm text-white">
                <span className="grid place-items-center h-7 w-7 rounded-md bg-[#0E9E90]/15 text-[#2DD4BF] font-mono text-xs">L{lv.level}</span>
                Level {lv.level}
              </span>
              <span className="flex items-center gap-3">
                <span className="font-mono text-xs text-slate-400">{lv.members} members</span>
                <ChevronDown size={16} className={`text-slate-500 transition-transform ${on ? "rotate-180" : ""}`} />
              </span>
            </button>
            <AnimatePresence initial={false}>
              {on && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                  <div className="px-4 pb-4">
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="rounded-lg bg-black/30 border border-white/5 p-2.5 text-center"><p className="font-mono text-lg text-white">{lv.members}</p><p className="text-[9px] uppercase font-mono text-slate-500">Members</p></div>
                      <div className="rounded-lg bg-black/30 border border-white/5 p-2.5 text-center"><p className="font-mono text-lg text-[#00E676]">{lv.active}</p><p className="text-[9px] uppercase font-mono text-slate-500">Active</p></div>
                      <div className="rounded-lg bg-black/30 border border-white/5 p-2.5 text-center"><p className="font-mono text-sm text-white mt-1">{fmtCents(lv.volume_cents)}</p><p className="text-[9px] uppercase font-mono text-slate-500">Sales Vol.</p></div>
                    </div>
                    {dots > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from({ length: dots }).map((_, i) => (
                          <span key={i} className={`h-3 w-3 rounded-sm ${i < activeDots ? "bg-[#00E676]" : "bg-white/15"}`} title={i < activeDots ? "Active" : "Member"} />
                        ))}
                        {lv.members > 24 && <span className="text-[10px] font-mono text-slate-500 self-center">+{lv.members - 24}</span>}
                      </div>
                    ) : (
                      <p className="text-xs font-mono text-slate-600">No members at this level yet.</p>
                    )}
                    <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#0E9E90] to-[#00E676]" style={{ width: `${((lv.volume_cents || 0) / maxVol) * 100}%` }} />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}


/* Interactive radial referral network map (7 levels) */
export function ReferralNetwork({ levels = [] }) {
  const [sel, setSel] = useState(1);
  const C = 190, SIZE = 380, CAP = 12;
  const LV = Array.from({ length: 7 }, (_, i) => {
    const d = levels.find((l) => l.level === i + 1) || {};
    return { level: i + 1, members: d.members || 0, active: d.active || 0, volume_cents: d.volume_cents || 0 };
  });
  const ringR = (lv) => 26 + lv * 22;
  const selData = LV.find((l) => l.level === sel);
  const totalMembers = LV.reduce((a, b) => a + b.members, 0);

  const dots = (l) => {
    const n = Math.min(CAP, l.members);
    if (!n) return [];
    const step = 360 / n, off = l.level * 14, r = ringR(l.level);
    return Array.from({ length: n }, (_, i) => {
      const a = ((off + i * step) * Math.PI) / 180;
      return { x: C + r * Math.cos(a), y: C + r * Math.sin(a), active: i < Math.min(n, l.active) };
    });
  };

  return (
    <div className="grid md:grid-cols-2 gap-6 items-center" data-testid="referral-network">
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full max-w-[380px] mx-auto">
        <defs>
          <radialGradient id="rn-core" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#0E9E90" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0E9E90" stopOpacity="0" />
          </radialGradient>
        </defs>
        {LV.map((l) => (
          <circle key={l.level} cx={C} cy={C} r={ringR(l.level)} fill="none"
            stroke={sel === l.level ? "rgba(45,212,191,0.6)" : "rgba(148,163,184,0.12)"} strokeWidth={sel === l.level ? 1.5 : 1}
            className="cursor-pointer" onClick={() => setSel(l.level)} />
        ))}
        {/* spokes for selected */}
        {dots(selData).map((d, i) => (
          <line key={`s${i}`} x1={C} y1={C} x2={d.x} y2={d.y} stroke="rgba(45,212,191,0.2)" strokeWidth="0.75" />
        ))}
        {/* dots for all levels */}
        {LV.map((l) => dots(l).map((d, i) => (
          <circle key={`${l.level}-${i}`} cx={d.x} cy={d.y} r={sel === l.level ? 4 : 3}
            fill={d.active ? "#00E676" : "#475569"} stroke={sel === l.level ? "#fff" : "none"} strokeWidth="0.5"
            className="cursor-pointer transition-all" onClick={() => setSel(l.level)} />
        )))}
        <circle cx={C} cy={C} r="44" fill="url(#rn-core)" />
        <circle cx={C} cy={C} r="24" fill="#08090F" stroke="rgba(14,158,144,0.6)" strokeWidth="1.5" />
        <text x={C} y={C - 2} textAnchor="middle" fill="#fff" fontSize="11" fontFamily="'JetBrains Mono',monospace" fontWeight="bold">YOU</text>
        <text x={C} y={C + 11} textAnchor="middle" fill="#2DD4BF" fontSize="8" fontFamily="'JetBrains Mono',monospace">{totalMembers} total</text>
      </svg>

      <div>
        <p className="text-xs text-slate-500 mb-3">Click a ring or level to inspect its production. Green nodes are licensed / active members.</p>
        <div className="space-y-1.5">
          {LV.map((l) => {
            const on = sel === l.level;
            return (
              <button key={l.level} onClick={() => setSel(l.level)} data-testid={`refnet-level-${l.level}`}
                className={`w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${on ? "border-[#0E9E90]/50 bg-[#0E9E90]/[0.06]" : "border-white/5 bg-black/20 hover:border-white/10"}`}>
                <span className="flex items-center gap-2.5">
                  <span className={`grid place-items-center h-6 w-6 rounded-md font-mono text-[10px] ${on ? "bg-[#0E9E90]/20 text-[#2DD4BF]" : "bg-white/5 text-slate-400"}`}>L{l.level}</span>
                  <span className="text-sm text-white">Level {l.level}</span>
                </span>
                <span className="font-mono text-xs text-slate-400">{l.members} · <span className="text-[#00E676]">{l.active} active</span></span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-white/5 bg-black/30 p-2.5 text-center"><p className="font-mono text-lg text-white">{selData.members}</p><p className="text-[9px] uppercase font-mono text-slate-500">Members</p></div>
          <div className="rounded-lg border border-white/5 bg-black/30 p-2.5 text-center"><p className="font-mono text-lg text-[#00E676]">{selData.active}</p><p className="text-[9px] uppercase font-mono text-slate-500">Active</p></div>
          <div className="rounded-lg border border-white/5 bg-black/30 p-2.5 text-center"><p className="font-mono text-sm text-white mt-1">{fmtCents(selData.volume_cents)}</p><p className="text-[9px] uppercase font-mono text-slate-500">Sales Vol.</p></div>
        </div>
      </div>
    </div>
  );
}

